//
// # Schematic Changes
//

// NPM Imports
import typia from "typia";

// Local Imports
import { validate as v, change as c } from "CoreUtils";
import type * as schema from "GenAlphaSchema";
import type { InstanceSpec } from "./instance";
import type { WireSegment } from "./wire";
import type { SchPinSpec } from "./schport";

export type SchematicChange = c.Change<Child> & { contentKind: "schematic" };

export const SchematicChange = {
  // Create an `Add` change
  add: (child: Child) =>
    ({
      contentKind: "schematic",
      changeKind: "add",
      data: child,
    } as const),
  // Create an `Update` change
  update: (id: number, data: c.Update<Child>) =>
    ({
      contentKind: "schematic",
      changeKind: "update",
      id,
      data,
    } as const),
  // Create a `Remove` change
  remove: (childKind: Child["targetKind"], id: number) =>
    ({
      contentKind: "schematic",
      changeKind: "remove",
      id,
      data: { targetKind: childKind },
    } as const),
  validator: new v.Validator(v.createValidate<SchematicChange>()),
  stringify: typia.json.createStringify<SchematicChange>(),
};

// Constraint spec for change operations
// Uses numeric id like other entities (IdMap assigns IDs on add)
export interface ConstraintSpec {
  id?: number; // Assigned by IdMap on add, required for update/remove
  description?: string;
  constraintType?: string;
  firstInstances?: string[];
  secondInstances?: string[];
  groupId?: number; // Explicit group assignment (skips auto-group creation)
}

// Constraint group spec for change operations
export interface ConstraintGroupSpec {
  id?: number;
  description?: string;
  symmetryDirection?: "vertical" | "horizontal";
  symmetryAxisPos?: number;
}

// Schematic Child Elements
// with targetKind change tags
type Child =
  | (InstanceSpec & { targetKind: "instance" })
  | (SchPinSpec & { targetKind: "schpin" })
  | (WireSegment & { targetKind: "wire" })
  | (schema.core.Element & { targetKind: "graphics" })
  | (ConstraintSpec & { targetKind: "constraint" })
  | (ConstraintGroupSpec & { targetKind: "constraint_group" });
